import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { PendingQuotationRequest, actions } from 'entities';

import { Alert, LabeledTextarea, Button, Column, Row, Label, Card } from 'components/Blocks';
import { MonetaryInput } from 'components/Inputs';
import { PropertyWithIcon } from 'components/PropertyWithIcon';

import { EarthIcon, CircleDollarIcon, UserIcon, SendIcon } from 'components/Icons';

import { CreatedBy, ProjectLink } from '../components';

import { formatCurrency } from 'utils/text';

interface RequestProps {
  request: PendingQuotationRequest;
  removeSelf: () => void;
}

export function SingleQuotationRequest({ request, removeSelf }: RequestProps) {
  const {
    clientName,
    id,
    project,
    valueInProjectCurrency,
    projectCurrency,
    currency,
    comment,
    country,
  } = request;

  const [value, setValue] = useState('');
  const [reviewComment, setComment] = useState('');

  const { t } = useLang();

  const numericValue = Number(value);

  const answerRequest = actions.invoice.answerQuotationRequest.use({
    block: !Number(value) || !comment.trim(),

    onSuccess: removeSelf,

    params: {
      project: project.id,
      request: id,
      value,
      comment: reviewComment,
    },
  });

  return (
    <Card flow="column" className="p-5">
      <Row gap="2">
        <CreatedBy id={project.createdBy} />

        <ProjectLink
          id={project.id}
          text={<span className="text-lg font-semibold text-primary">{project.name}</span>}
        />
      </Row>

      <p className="font-medium">
        <strong>{t('projects-client')}</strong>:{project.clientName}
      </p>

      <Column className="border border-dashed p-4" gap="6">
        <p className="text-center font-medium">{t('user-request')}</p>

        <PropertyWithIcon icon={<UserIcon />} name={t('client')} value={clientName} />

        <PropertyWithIcon icon={<EarthIcon />} name={t('country-to-invoice-to')} value={country} />

        <PropertyWithIcon
          icon={<CircleDollarIcon />}
          name={t('value-in-project-currency')}
          value={formatCurrency(valueInProjectCurrency, projectCurrency)}
        />

        <Column gap="2">
          <Label>{t('quote-value')}</Label>

          <MonetaryInput
            currency={currency}
            currencyDisabled
            onChangeDirect={setValue}
            name="request-value"
            value={value}
          />
        </Column>

        <Alert level="info" title={t('attention')}>
          {t(
            'please-consider-each-client-rate-card-all-the-information-provided-and-currency-rate-on-your-analysis-this-is-the-value-the-client-will-be-billed',
          )}
        </Alert>
      </Column>

      <Column gap="2">
        <p className="font-semibold">{t('user-comment')}</p>

        <p className="rounded-lg border bg-muted p-3">{comment}</p>
      </Column>

      <Column gap="6">
        <LabeledTextarea
          label={t('comments') as string}
          value={reviewComment}
          onChange={({ target: { value } }) => setComment(value)}
        />

        <Button disabled={!numericValue || !reviewComment.trim()} {...answerRequest.buttonProps}>
          <SendIcon />
          {t('answer-request')}
        </Button>
      </Column>
    </Card>
  );
}
